/**
 * Google-Bewertungsbitte (sql/542): der Schalter „Bewertungsbitte senden“ je Vorgang.
 *
 * Antoine, 26.09.2026: neutral, OHNE Grund (weder hier noch in den Daten), gut sichtbar im Vorgang
 * und in der Freigabe, Vorgabe an, bis zum Versand jederzeit umschaltbar — auch zurück. Der
 * Schalter kann eine Bitte nur ZUSAETZLICH unterdruecken; ohne Einwilligung des Gastes geht nie eine
 * hinaus (das erzwingt die Datenbank). Deshalb steht der Einwilligungsstand direkt darunter.
 *
 * Eigenstaendige Komponente (holt und schreibt ihren Stand selbst), damit die neue Vorgangsseite
 * „Salon“ sie unveraendert einhaengen kann.
 */
import { useState } from "react";
import { useApi } from "../hooks/useApi";
import { apiFetch, toApiError } from "../lib/api";
import { Icon, Toggle } from "./components";

export interface BewertungsLage {
  aktiv: boolean;
  senden: boolean;
  einwilligungAm: string | null;
  kanal: "angebotsseite" | "kundenportal" | "formular" | null;
  versandtag: string | null;
  zustand: "offen" | "freigegeben" | "versendet" | "verworfen" | "fehlgeschlagen" | null;
  versendetAm: string | null;
  geklicktAm: string | null;
  abgemeldet: boolean;
}

const KLEIN = "font-body-sm text-[12.5px] leading-relaxed text-secondary dark:text-secondary-fixed-dim";

/** „Mi, 30.09.“ — Wochentag kurz, Tag und Monat. */
export function versandtagText(iso: string): string {
  const d = new Date(`${iso}T00:00:00Z`);
  const wt = new Intl.DateTimeFormat("de-DE", { weekday: "short", timeZone: "UTC" }).format(d).replace(".", "");
  const tm = new Intl.DateTimeFormat("de-DE", { day: "2-digit", month: "2-digit", timeZone: "UTC" }).format(d);
  return `${wt}, ${tm}`;
}
const datumText = (iso: string) => new Date(iso).toLocaleDateString("de-DE", { day: "2-digit", month: "2-digit", year: "numeric", timeZone: "Europe/Berlin" });

/** Die Zeilen unter dem Schalter — rein, damit der Web-Test sie ohne Netz pruefen kann. */
export function bewertungsZeilen(s: BewertungsLage): { einwilligung: string; status: string | null } {
  const einwilligung = s.einwilligungAm
    ? `Gast hat zugestimmt am ${datumText(s.einwilligungAm)} (${s.kanal === "kundenportal" ? "Kundenportal" : s.kanal === "formular" ? "Anfrageformular" : "Angebotsseite"}).`
    : "Ohne Zustimmung des Gastes wird nichts versendet.";
  let status: string | null = null;
  if (s.zustand === "versendet") status = `Bewertungsbitte verschickt am ${s.versendetAm ? datumText(s.versendetAm) : "–"}.${s.geklicktAm ? " Der Gast hat den Google-Knopf geöffnet." : ""}`;
  else if (s.abgemeldet) status = "Der Gast hat solche E-Mails abbestellt.";
  else if (s.zustand === "verworfen") status = "Diese Bewertungsbitte wurde nicht verschickt.";
  else if (!s.senden) status = "Für diesen Vorgang wird keine Bitte gesendet.";
  else if (s.einwilligungAm && s.versandtag) status = `Bewertungsbitte geht am ${versandtagText(s.versandtag)} raus (nach Freigabe).`;
  return { einwilligung, status };
}

export function BewertungsbitteSchalter({ eventId, kompakt = false }: { eventId: string; kompakt?: boolean }) {
  const { data, reload } = useApi<BewertungsLage>(`/api/events/${eventId}/bewertungsbitte`);
  const [stand, setStand] = useState<BewertungsLage | null>(null);
  const [fehler, setFehler] = useState<string | null>(null);
  const [speichert, setSpeichert] = useState(false);
  const s = stand ?? data;
  // Funktion beim Betrieb aus oder kein Google-Link: nichts anzeigen.
  if (!s || !s.aktiv) return null;
  const { einwilligung, status } = bewertungsZeilen(s);
  const gesperrt = s.zustand === "versendet";

  async function umschalten(senden: boolean) {
    setSpeichert(true);
    setFehler(null);
    try {
      const res = await apiFetch(`/api/events/${eventId}/bewertungsbitte`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ senden }),
      });
      if (!res.ok) throw await toApiError(res);
      setStand(((await res.json()) as { data: BewertungsLage }).data);
    } catch (e) {
      setFehler(e instanceof Error ? e.message : "Speichern fehlgeschlagen.");
      reload();
    } finally {
      setSpeichert(false);
    }
  }

  return (
    <div
      data-testid="bewertungsbitte-schalter"
      className={kompakt ? "flex items-start gap-3" : "flex items-start gap-3 rounded-xl border border-[#F0E7D6] bg-white px-4 py-3 dark:border-white/[0.06] dark:bg-inverse-surface"}
    >
      {!kompakt && <Icon name="reviews" className="mt-0.5 shrink-0 text-[20px] text-primary dark:text-[#f0b49b]" />}
      <div className="min-w-0 flex-1">
        <div className="flex items-center justify-between gap-3">
          <span className="font-body-md text-body-md font-semibold text-on-background dark:text-inverse-on-surface">Bewertungsbitte senden</span>
          <Toggle checked={s.senden} onChange={umschalten} label="Bewertungsbitte senden" disabled={speichert || gesperrt} />
        </div>
        <p className={KLEIN} data-testid="bewertungsbitte-einwilligung">{einwilligung}</p>
        {status && <p className={KLEIN} data-testid="bewertungsbitte-status">{status}</p>}
        {fehler && <p className="font-body-sm text-[12.5px] text-error">{fehler}</p>}
      </div>
    </div>
  );
}
